/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { useRef } from 'react';
import { useIsomorphicLayoutEffect } from '@/hooks/useIsomorphicLayoutEffect';

/**
 * Custom hook that observes element resize events with debouncing to prevent excessive updates.
 * Note: Does NOT read initial width - use a callback ref for that.
 * Fires callback on first ResizeObserver event, then debounces subsequent resize events.
 *
 * @param elementRef - React ref object to the element to observe
 * @param callback - Function to call with the element's width when resize occurs
 * @param delay - Debounce delay in milliseconds (default: 150ms)
 */
export const useDebouncedResizeObserver = (
  elementRef: React.RefObject<HTMLElement | null>,
  callback: (width: number) => void,
  delay = 150,
) => {
  const observerRef = useRef<ResizeObserver | null>(null);
  const isFirstObservation = useRef(true);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);

  useIsomorphicLayoutEffect(() => {
    const element = elementRef.current;
    if (!element) {
      return;
    }

    isFirstObservation.current = true;
    isMountedRef.current = true;

    const debouncedCallback = (width: number) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        if (isMountedRef.current) {
          callback(width);
        }
        timeoutRef.current = null;
      }, delay);
    };

    observerRef.current = new ResizeObserver((entries) => {
      if (!entries || !entries.length) {
        return;
      }
      const width = entries[0].contentRect.width;

      if (isFirstObservation.current) {
        callback(width);
        isFirstObservation.current = false;
      } else {
        debouncedCallback(width);
      }
    });

    observerRef.current.observe(element);

    return () => {
      isMountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [elementRef, callback, delay]);
};
